import {createSlice, PayloadAction, createAsyncThunk, createSelector} from '@reduxjs/toolkit';
import type {RootState} from '../store';
import {UserService} from '../services/userService';
import {setSubPatients} from './patientSlice';

// Helper function to check if we're in staging or development environment
const isStagingEnvironment = (): boolean => {
    // Check environment variables
    const viteEnv = import.meta.env?.VITE_NODE_ENV;
    const nodeEnv = import.meta.env?.NODE_ENV;

    return viteEnv === 'staging' || viteEnv === 'development' || nodeEnv === 'staging' || nodeEnv === 'development';
};

// Helper function to get URL parameter value
const getUrlParameter = (name: string): string | null => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(name);
};

// Helper function to get force subscription status from URL in staging
const getForceSubscriptionStatus = (): boolean | null => {
    if (!isStagingEnvironment()) {
        return null; // Only work in staging/dev environments
    }

    const subParam = getUrlParameter('sub');
    if (subParam === null) {
        return null; // No 'sub' parameter found
    }

    // Convert string to boolean
    if (subParam.toLowerCase() === 'true') {
        return true;
    } else if (subParam.toLowerCase() === 'false') {
        return false;
    }

    return null; // Invalid value, use default behavior
};

// TESTING OVERRIDE - Set to true/false to test subscription states
// In staging environments, this can be controlled via URL parameter "sub"
const FORCE_SUBSCRIPTION_STATUS: boolean | null = getForceSubscriptionStatus();

interface Test {
    id: number;
    elis_id: number;
    oid: string;
    createdDate: string;
    statusDate: string;
    amendedDate: string;
    status: string;
    resulted: string;
    final: boolean;
    issueCount: number;
    issues: string;
    customer_id: number;
    firstName: string;
    lastName: string;
    birthdate: string;
    sent: boolean;
    appointmentDate: string;
    appointmentLocation: string;
    appointmentType: string | [any, any];
    appointmentDuration: string;
    appointmentCalendar: string;
    results: Result[];
    recommendations: any[];
}

interface FetchTestsResponse {
    orders: Test[];
    categoryScores: Record<string, number>;
    subscription: boolean;
}

interface Result {
    test_name: string;
    test_code: string;
    result: string;
    refRange: string;
    panel_name: string;
    panel_code: string;
    units: string;
    notes: string;
    abnormal?: string;
    statusDate?: string;
}

// Define a type for the slice state
interface TestState {
    tests: Test[];
    loading: boolean;
    categoryScores: Record<string, number>;
    subscription: boolean;
}

// Define the initial state using that type
const initialState: TestState = {
    tests: [],
    loading: false,
    categoryScores: {},
    subscription: true, // Default to true until we get API response
};

export const fetchTests = createAsyncThunk('test/fetchTests', async (_: void, api) => {
    // Use UserService which automatically includes the token from apiClient
    const res = await UserService.getUserData();

    const uniquePatients: any = {};
    const newPatients: any = [];

    const {patient} = api.getState() as RootState;

    // Only add current patient to the list if it has valid data
    if (
        patient.currentPatient.id &&
        patient.currentPatient.id > 0 &&
        patient.currentPatient.firstName &&
        patient.currentPatient.lastName
    ) {
        const currentPatientFullName = patient.currentPatient.fullName.toLowerCase();
        uniquePatients[currentPatientFullName] = true;

        newPatients.push({
            id: patient.currentPatient.id,
            firstName: patient.currentPatient.firstName?.trim(),
            lastName: patient.currentPatient.lastName?.trim(),
            fullName: patient.currentPatient?.fullName?.trim(),
            birthdate: patient.currentPatient.birthdate,
        });
    }

    // Handle both response structures - check if we have the new structured response
    const orders: Test[] = res?.orders ?? [];
    const categoryScores = res?.categoryScores ?? {};
    const subscription =
        FORCE_SUBSCRIPTION_STATUS !== null ? FORCE_SUBSCRIPTION_STATUS : ((res as any)?.subscription ?? true); // Default to true if not specified

    // Keep real data in Redux - dashboard components will handle dummy data conditionally

    // Extract unique patients from test data
    for (let i = 0; i < orders.length; i++) {
        if (!orders[i].firstName || !orders[i].lastName) {
            continue;
        }
        const patient = `${orders[i].firstName} ${orders[i].lastName}`.toLowerCase().trim();
        if (!uniquePatients[patient]) {
            uniquePatients[patient] = true;
            newPatients.push({
                id: orders[i].id,
                firstName: orders[i].firstName?.trim(),
                lastName: orders[i].lastName?.trim(),
                fullName: patient || '',
                birthdate: orders[i].birthdate,
            });
        }
    }

    api.dispatch(setSubPatients(newPatients));

    // Return structured response
    const payload: FetchTestsResponse = {
        orders,
        categoryScores,
        subscription,
    };

    return payload;
});

export const testSlice = createSlice({
    name: 'tests',
    // `createSlice` will infer the state type from the `initialState` argument
    initialState,
    reducers: {
        resetTestsState: (state) => {
            state.tests = [];
            state.loading = false;
            state.categoryScores = {};
            state.subscription = true;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchTests.fulfilled, (state, action: PayloadAction<FetchTestsResponse>) => {
                state.loading = false;
                if (action.payload) {
                    state.tests = action.payload.orders ?? [];
                    state.categoryScores = action.payload.categoryScores ?? {};
                    state.subscription = action.payload.subscription ?? true;
                }
            })
            .addCase(fetchTests.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchTests.rejected, (state) => {
                state.loading = false;
                // Don't clear existing data on error, just stop loading
            });
    },
});

export const {resetTestsState} = testSlice.actions;

// Other code such as selectors can use the imported `RootState` type
export const selectTests = (state: RootState) => state.tests;
export const selectTest = (state: RootState, idx: number) => state.tests.tests.find((t) => t.id === idx);

const selectAllTests = (state: RootState) => state.tests.tests;
const selectNameParam = (_state: RootState, name: string) => name;

export const selectTestsByName = createSelector([selectAllTests, selectNameParam], (tests, name) => {
    const normalizedName = name.toLowerCase().trim();
    return tests.filter(
        (t) => `${t.firstName?.toLowerCase().trim()} ${t.lastName?.toLowerCase().trim()}` === normalizedName
    );
});

export const selectTestsWithRecommendations = createSelector([selectAllTests], (tests) =>
    tests.filter((t) => Array.isArray(t.recommendations) && t.recommendations.length > 0)
);
export const selectCategoryScores = (state: RootState) => state.tests.categoryScores;
export const selectSubscription = (state: RootState) => state.tests.subscription;

export default testSlice.reducer;
